import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';

const ExperienceContainer = styled.div`
  padding: 50px;
  color: #727272;
  font-family: 'Raleway', sans-serif;
`;


const BlockTitle = styled.h3`
  font-size: 20px;
  line-height: 26px;
  margin: 0 0 30px 0;
  font-weight: 500;
  text-transform: uppercase;
  color: #333;
`;

const ProfileSection = styled.div`
  margin-bottom: 50px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -15px;
`;

const Col = styled.div<{ md?: number }>`
  flex: ${props => props.md ? `0 0 ${(props.md / 12) * 100}%` : '1'};
  padding: 0 15px;
  
  @media (max-width: 768px) {
    flex: 0 0 100%;
  }
`;

const ProfileImg = styled.div`
  text-align: center;
  margin-bottom: 30px;
  
  img {
    width: 200px;
    height: 200px;
    border-radius: 50%;
    object-fit: cover;
  }
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  
  li {
    margin-bottom: 10px;
    line-height: 30px;
    
    strong {
      color: #333;
    }
  }
`;

const Timeline = styled.div`
  position: relative;
`;

const ExpHolder = styled.div`
  position: relative;
`;

const Exp = styled.div`
  background: #f8f9fa;
  padding: 30px;
  margin-bottom: 30px;
  border-left: 3px solid #F34452;
`;

const HGroup = styled.div`
  margin-bottom: 20px;
  
  h4 {
    font-size: 16px;
    line-height: 24px;
    margin: 0 0 5px 0;
    color: #333;
    text-transform: uppercase;
    font-weight: 700;
    
    .work-place {
      font-weight: 400;
      color: #F34452;
    }
    
    a {
      color: #F34452;
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
  
  h5 {
    font-size: 15px;
    line-height: 32px;
    margin: 0;
    color: #727272;
    font-weight: 400;
    
    .current {
      background: #2ecc71;
      color: #FFF;
      padding: 2px 8px;
      font-size: 12px;
      border-radius: 3px;
    }
  }
`;

const ExpList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  
  li {
    margin-bottom: 10px;
    line-height: 24px;
    position: relative;
    padding-left: 20px;
    
    &:before {
      content: '•';
      color: #F34452;
      position: absolute;
      left: 0;
      font-weight: bold;
    }
  }
`;

const EmailSection = styled.div`
  text-align: center;
  padding: 30px 0;
  
  a {
    background: #F34452;
    color: #FFF;
    padding: 15px 30px;
    text-decoration: none;
    text-transform: lowercase;
    font-weight: 700;
    
    &:hover {
      background: #d63946;
      color: #FFF;
    }
  }
  
  small {
    display: block;
    margin-top: 10px;
    color: #727272;
  }
`;

const Experience: React.FC = () => {
  const { t } = useLanguage();
  
  return (
    <ExperienceContainer>
          <ProfileSection>
            <BlockTitle>{t('exp.profile')}</BlockTitle>
            <Row>
              <Col md={7}>
                <p>{t('exp.profileDesc1')}</p>
                <br />
                <p>{t('exp.profileDesc2')}</p>
              </Col>
              <Col md={5}>
                <ProfileImg>
                  <img loading="lazy" decoding="async" src="/images/bg/me.webp" alt="Yuanyuan Li" />
                </ProfileImg>
                <InfoList>
                  <li><strong>{t('exp.name')} : </strong>{t('exp.personalInfo.name')}</li>
                  <li><strong>{t('exp.location')} : </strong>{t('exp.personalInfo.location')}</li>
                  <li><strong>{t('exp.email')} : </strong>{t('exp.personalInfo.email')}</li>
                  <li><strong>{t('exp.skype')} : </strong><a href="https://www.linkedin.com/in/yuanyuanmaggie" target="_blank" rel="noopener noreferrer" style={{color: '#F34452'}}>{t('exp.personalInfo.skype')}</a></li>
                  <li><strong>{t('exp.interest')} : </strong>{t('exp.personalInfo.interest')}</li>
                </InfoList>
              </Col>
            </Row>
          </ProfileSection>
          
          <div>
            <BlockTitle>{t('exp.experience')}</BlockTitle>
            <Timeline>
              <ExpHolder>
                <Exp>
                  <HGroup>
                    <h4>Vice President, Software Engineering Manager <span className="work-place"> @ Two Sigma <a href="https://www.venn.twosigma.com/" target="_blank" rel="noopener noreferrer">(Venn)</a></span></h4>
                    <h5>July 2018 - <span className="current">{t('exp.current')}</span></h5>
                  </HGroup>
                  <ExpList>
                    <li>{t('exp.twosigma.bullet1')}</li>
                    <li>{t('exp.twosigma.bullet2')}</li>
                    <li>{t('exp.twosigma.bullet3')}</li>
                    <li>{t('exp.twosigma.bullet4')}</li>
                    <li>{t('exp.twosigma.bullet5')}</li>
                    <li>{t('exp.twosigma.bullet6')}</li>
                    <li>{t('exp.twosigma.bullet7')}</li>
                    <li>{t('exp.twosigma.bullet8')}</li>
                    <li>{t('exp.twosigma.bullet9')}</li>
                  </ExpList>
                </Exp>
                
                <Exp>
                  <HGroup>
                    <h4>Front End Engineer <span className="work-place"> @ Jet.com (Walmart)</span></h4>
                    <h5>Jan 2017 - July 2018</h5>
                  </HGroup>
                  <ExpList>
                    <li>{t('exp.jet.bullet1')}</li>
                    <li>{t('exp.jet.bullet2')}</li>
                    <li>{t('exp.jet.bullet3')}</li>
                    <li>{t('exp.jet.bullet4')}</li>
                    <li>{t('exp.jet.bullet5')}</li>
                    <li>{t('exp.jet.bullet6')}</li>
                    <li>{t('exp.jet.bullet7')}</li>
                    <li>{t('exp.jet.bullet8')}</li>
                    <li>{t('exp.jet.bullet9')}</li>
                  </ExpList>
                </Exp>
                
                <Exp>
                  <HGroup>
                    <h4>Web Developer <span className="work-place"> @ SupplyHouse.com</span></h4>
                    <h5>Aug 2016 - Jan 2017</h5>
                  </HGroup>
                  <ExpList>
                    <li>{t('exp.supply.bullet1')}</li>
                    <li>{t('exp.supply.bullet2')}</li>
                    <li>{t('exp.supply.bullet3')}</li>
                    <li>{t('exp.supply.bullet4')}</li>
                    <li>{t('exp.supply.bullet5')}</li>
                  </ExpList>
                </Exp>
                
                <Exp>
                  <HGroup>
                    <h4>Full Stack Developer <span className="work-place"> @ Yumso Inc</span></h4>
                    <h5>May 2016 - 2017</h5>
                  </HGroup>
                  <ExpList>
                    <li>{t('exp.yumso.bullet1')}</li>
                    <li>{t('exp.yumso.bullet2')}</li>
                    <li>{t('exp.yumso.bullet3')}</li>
                  </ExpList>
                </Exp>
                
                <Exp>
                  <HGroup>
                    <h4>Project Assistant<span className="work-place"> @ PwC</span></h4>
                    <h5>March 2015 - July 2015</h5>
                  </HGroup>
                  <ExpList>
                    <li>{t('exp.pwc.bullet1')}</li>
                    <li>{t('exp.pwc.bullet2')}</li>
                  </ExpList>
                </Exp>
                
                <Exp>
                  <HGroup>
                    <h4>Project Assistant<span className="work-place"> @ SAP</span></h4>
                    <h5>March 2015 - July 2015</h5>
                  </HGroup>
                  <ExpList>
                    <li>{t('exp.sap.bullet1')}</li>
                    <li>{t('exp.sap.bullet2')}</li>
                  </ExpList>
                </Exp>
              </ExpHolder>
            </Timeline>
          </div>
          
          <div>
            <BlockTitle>{t('exp.education')}</BlockTitle>
            <Timeline>
              <ExpHolder>
                <Exp>
                  <HGroup>
                    <h4>Cornell University <span className="work-place">, Ithaca, NY</span></h4>
                    <h4>{t('exp.cornell.degree')}</h4>
                    <h5>Aug 2015 - May 2016</h5>
                  </HGroup>
                  <ExpList>
                    <li>{t('exp.cornell.gpa')}</li>
                    <li>{t('exp.cornell.courses')}</li>
                  </ExpList>
                </Exp>
                
                <Exp>
                  <HGroup>
                    <h4>University of International Business and Economics <span className="work-place">, Beijing</span></h4>
                    <h4>{t('exp.uibe.master.degree')}</h4>
                    <h5>Sep 2015 - July 2016</h5>
                  </HGroup>
                  <ExpList>
                    <li>{t('exp.uibe.master.gpa')}</li>
                    <li>{t('exp.uibe.master.courses')}</li>
                  </ExpList>
                </Exp>
                
                <Exp>
                  <HGroup>
                    <h4>University of International Business and Economics <span className="work-place">, Beijing</span></h4>
                    <h4>{t('exp.uibe.bachelor.degree')}</h4>
                    <h5>Sep 2010 - July 2014</h5>
                  </HGroup>
                  <ExpList>
                    <li>{t('exp.uibe.bachelor.gpa')}</li>
                    <li>{t('exp.uibe.bachelor.courses')}</li>
                  </ExpList>
                </Exp>
              </ExpHolder>
            </Timeline>
          </div>
          
          <div>
            <BlockTitle>{t('exp.awards')}</BlockTitle>
            <Timeline>
              <ExpHolder>
                <Exp>
                  <HGroup>
                    <h4>{t('exp.award.guanghua')}</h4>
                    <h5>2015</h5>
                  </HGroup>
                </Exp>
                <Exp>
                  <HGroup>
                    <h4>{t('exp.award.beijing.graduate')}</h4>
                    <h5>2014</h5>
                  </HGroup>
                </Exp>
                <Exp>
                  <HGroup>
                    <h4>{t('exp.award.academic')}</h4>
                    <h5>2013</h5>
                  </HGroup>
                </Exp>
                <Exp>
                  <HGroup>
                    <h4>{t('exp.award.leadership')}</h4>
                    <h5>2013</h5>
                  </HGroup>
                </Exp>
                <Exp>
                  <HGroup>
                    <h4>{t('exp.award.national')}</h4>
                    <h5>2012</h5>
                  </HGroup>
                </Exp>
                <Exp>
                  <HGroup>
                    <h4>{t('exp.award.beijing.merit')}</h4>
                    <h5>2012</h5>
                  </HGroup>
                </Exp>
                <Exp>
                  <HGroup>
                    <h4>{t('exp.award.ebusiness')}</h4>
                    <h5>2011</h5>
                  </HGroup>
                </Exp>
              </ExpHolder>
            </Timeline>
          </div>
          
          <EmailSection>
            <a href="mailto:yuanyuanli2022@gmail.com">yuanyuanli2022@gmail.com</a>
            <small>{t('exp.contactMessage')}</small>
          </EmailSection>
    </ExperienceContainer>
  );
};

export default Experience;